import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ChallengeNotification({ challenge, onDismiss, onViewed }) {
  const [isVisible, setIsVisible] = useState(true);

  // évite double "viewed"
  const hasViewedRef = useRef(false);

  // refs pour timers
  const autoTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  // Reset quand on reçoit un nouveau challenge
  useEffect(() => {
    setIsVisible(true);
    hasViewedRef.current = false;
    clearTimers();
    return () => clearTimers();
  }, [challenge?.id, clearTimers]);

  // Auto-dismiss (ne marque PAS vu)
  useEffect(() => {
    if (!challenge?.id) return;

    autoTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      // fermeture auto => ne touche pas le compteur
      dismissTimerRef.current = setTimeout(() => {
        onDismiss?.(challenge.id);
      }, 300);
    }, 10000);

    return () => clearTimers();
  }, [challenge?.id, onDismiss, clearTimers]);

  const handleUserDismiss = useCallback(() => {
    if (!challenge?.id) {
      setIsVisible(false);
      dismissTimerRef.current = setTimeout(() => onDismiss?.(challenge?.id), 300);
      return;
    }

    setIsVisible(false);

    // clic utilisateur => marque "vu" une seule fois
    if (!hasViewedRef.current) {
      hasViewedRef.current = true;
      onViewed?.(challenge.id);
    }

    dismissTimerRef.current = setTimeout(() => {
      onDismiss?.(challenge.id);
    }, 300);
  }, [challenge?.id, onDismiss, onViewed]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed z-10 bg-[#13151D] rounded-lg shadow-2xl border-2 border-[#6E2BFF] p-3 cursor-pointer hover:bg-[#1B1E28] transition-all"
          style={{
            bottom: 'calc(4rem + 55px + 4rem + 0.5rem)',
            right: '1rem',
            maxWidth: '280px'
          }}
          onClick={() => {
            handleUserDismiss();
            if (window.__onChallengesClick) window.__onChallengesClick();
          }}
        >
          <div className="flex items-start gap-3">
            <div className="bg-[#6E2BFF] rounded-full p-2 flex-shrink-0">
              <Trophy className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white mb-1">Nouveau Challenge disponible</p>
              <p className="text-xs text-[#B3B6C2] line-clamp-2">{challenge?.title}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
